import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import dayjs from 'dayjs';
import PageHeader from '../components/PageHeader';
import LoadingFallback from '../components/LoadingFallback';
import EmptyState from '../components/EmptyState';
import StatusChip from '../components/StatusChip';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { Meet, MeetEvent, Registration } from '../api/types';

type Filter = 'all' | 'missing' | 'next' | 'track' | 'field';
type ResultTarget = { registration: Registration; event: MeetEvent };
const ATTEMPT_COUNT = 6;

export default function MeetDayPage() {
  const { id } = useParams();
  const meetId = Number(id);
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const [filter, setFilter] = useState<Filter>('all');
  const [resultTarget, setResultTarget] = useState<ResultTarget | null>(null);

  const meetQuery = useQuery<Meet>({ queryKey: ['meets', meetId], queryFn: async () => (await api.get(`/meets/${meetId}`)).data });
  const eventsQuery = useQuery<MeetEvent[]>({ queryKey: ['meets', meetId, 'events'], queryFn: async () => (await api.get(`/meet-events/by-meet/${meetId}`)).data });
  const registrationsQuery = useQuery<Registration[]>({
    queryKey: ['meets', meetId, 'meet-day-registrations'],
    enabled: !!eventsQuery.data,
    queryFn: async () => {
      const responses = await Promise.all((eventsQuery.data ?? []).map((ev) => api.get<Registration[]>(`/registrations/by-meet-event/${ev.id}`).then((r) => r.data)));
      return responses.flat();
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ reg, payload }: { reg: Registration; payload: any }) => (await api.patch(`/registrations/${reg.id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meets', meetId, 'meet-day-registrations'] }),
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const meet = meetQuery.data;
  const events = eventsQuery.data ?? [];
  const registrations = registrationsQuery.data ?? [];

  const byEvent = useMemo(() => {
    const map = new Map<number, Registration[]>();
    for (const reg of registrations) {
      const list = map.get(reg.meet_event_id) ?? [];
      list.push(reg);
      map.set(reg.meet_event_id, list);
    }
    return map;
  }, [registrations]);

  const sortedEvents = [...events].sort((a, b) => {
    const av = a.scheduled_start_at ? dayjs(a.scheduled_start_at).valueOf() : Number.MAX_SAFE_INTEGER;
    const bv = b.scheduled_start_at ? dayjs(b.scheduled_start_at).valueOf() : Number.MAX_SAFE_INTEGER;
    return av - bv;
  });
  const now = dayjs();
  const nextEvent = sortedEvents.find((ev) => !ev.scheduled_start_at || dayjs(ev.scheduled_start_at).isAfter(now.subtract(15, 'minute')));
  const filteredEvents = sortedEvents.filter((ev) => {
    const regs = byEvent.get(ev.id) ?? [];
    if (filter === 'missing') return regs.some((r) => r.check_in_status === 'missing' || !r.check_in_status);
    if (filter === 'next') return ev.scheduled_start_at && dayjs(ev.scheduled_start_at).isBefore(now.add(1, 'hour')) && dayjs(ev.scheduled_start_at).isAfter(now.subtract(30, 'minute'));
    if (filter === 'track') return ev.event_type.category === 'track' || ev.event_type.category === 'relay';
    if (filter === 'field') return ev.event_type.category === 'field';
    return true;
  });

  if (meetQuery.isLoading || eventsQuery.isLoading || registrationsQuery.isLoading) return <LoadingFallback />;
  if (!meet) return <EmptyState title="Meet not found" />;

  function setCheckIn(reg: Registration, check_in_status: string) {
    const payload: any = { check_in_status };
    if (check_in_status === 'scratched') payload.status = 'scratched';
    statusMutation.mutate({ reg, payload });
  }

  return (
    <Box>
      <PageHeader
        title={`${meet.name} Meet Day`}
        subtitle="Big buttons. Fast taps. Keep the team moving."
        actions={<Button component={RouterLink} to={`/meets/${meetId}/coach-pack`} startIcon={<PrintIcon />} variant="outlined">Coach Pack</Button>}
      />

      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, #0b5394 0%, #062f5a 100%)', color: '#fff' }}>
        <CardContent>
          <Typography variant="overline" sx={{ opacity: 0.8 }}>Now / Next</Typography>
          {nextEvent ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>{nextEvent.event_type.name}</Typography>
                <Typography sx={{ opacity: 0.85 }}>
                  {nextEvent.scheduled_start_at ? dayjs(nextEvent.scheduled_start_at).format('h:mm A') : 'Time TBD'} · {nextEvent.division}{nextEvent.area ? ` · ${nextEvent.area}` : ''}
                </Typography>
              </Box>
              <Chip label={`${byEvent.get(nextEvent.id)?.length ?? 0} athletes`} sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', alignSelf: { xs: 'flex-start', sm: 'center' } }} />
            </Stack>
          ) : (
            <Typography>No remaining scheduled events.</Typography>
          )}
        </CardContent>
      </Card>

      <ToggleButtonGroup value={filter} exclusive onChange={(_, v) => v && setFilter(v)} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="missing">Missing</ToggleButton>
        <ToggleButton value="next">Next hour</ToggleButton>
        <ToggleButton value="track">Track</ToggleButton>
        <ToggleButton value="field">Field</ToggleButton>
      </ToggleButtonGroup>

      <Stack spacing={2}>
        {filteredEvents.length === 0 && <EmptyState title="No events match this filter" />}
        {filteredEvents.map((ev) => {
          const regs = byEvent.get(ev.id) ?? [];
          return (
            <Card key={ev.id} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{ev.event_type.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ev.scheduled_start_at ? dayjs(ev.scheduled_start_at).format('ddd h:mm A') : 'Time TBD'} · {ev.division}{ev.age_group ? ` · ${ev.age_group}` : ''}{ev.area ? ` · ${ev.area}` : ''}
                    </Typography>
                  </Box>
                  <Chip size="small" label={`${regs.length} entries`} />
                </Stack>
                <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                  {regs.map((reg) => (
                    <Grid item xs={12} md={6} key={reg.id}>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>{reg.student.full_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {reg.heat_id ? `Heat ${reg.heat_id}` : 'No heat'}{reg.lane ? ` · Lane ${reg.lane}` : ''}{reg.seed_mark ? ` · Seed ${reg.seed_mark}` : ''}
                            </Typography>
                          </Box>
                          <StatusChip status={reg.status} />
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                          <Button size="small" variant={reg.check_in_status === 'here' ? 'contained' : 'outlined'} color="success" onClick={() => setCheckIn(reg, 'here')}>Here</Button>
                          <Button size="small" variant={reg.check_in_status === 'missing' || !reg.check_in_status ? 'contained' : 'outlined'} color="warning" onClick={() => setCheckIn(reg, 'missing')}>Missing</Button>
                          <Button size="small" variant={reg.check_in_status === 'scratched' ? 'contained' : 'outlined'} color="error" onClick={() => setCheckIn(reg, 'scratched')}>Scratch</Button>
                          <Button size="small" onClick={() => statusMutation.mutate({ reg, payload: { status: 'dns' } })}>DNS</Button>
                          <Button size="small" onClick={() => setResultTarget({ registration: reg, event: ev })}>
                            {ev.event_type.category === 'field' ? 'Field marks' : 'Result'}
                          </Button>
                        </Stack>
                        {reg.result?.mark && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Result: {reg.result.mark}{reg.result.place ? ` · place ${reg.result.place}` : ''}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <ResultDialog target={resultTarget} onClose={() => setResultTarget(null)} meetId={meetId} />
    </Box>
  );
}

function ResultDialog({ target, onClose, meetId }: { target: ResultTarget | null; onClose: () => void; meetId: number }) {
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const [mark, setMark] = useState('');
  const [place, setPlace] = useState('');
  const [wind, setWind] = useState('');
  const [notes, setNotes] = useState('');
  const [attempts, setAttempts] = useState<string[]>(Array(ATTEMPT_COUNT).fill(''));
  const [manualMarkEdited, setManualMarkEdited] = useState(false);

  const registration = target?.registration ?? null;
  const event = target?.event ?? null;
  const isFieldEvent = event?.event_type.category === 'field';
  const bestAttempt = bestFieldAttempt(attempts);

  useEffect(() => {
    if (registration) {
      setMark(registration.result?.mark || '');
      setPlace(registration.result?.place ? String(registration.result.place) : '');
      setWind(registration.result?.wind ? String(registration.result.wind) : '');
      setNotes(registration.result?.notes || '');
      setAttempts(parseAttempts(registration.result?.notes));
      setManualMarkEdited(false);
    }
  }, [registration]);

  const save = useMutation({
    mutationFn: async () => api.put(`/results/by-registration/${registration?.id}`, {
      mark: isFieldEvent ? (manualMarkEdited && mark.trim() ? mark : bestAttempt || mark) : mark,
      place: place ? Number(place) : null,
      wind: wind ? Number(wind) : null,
      notes: isFieldEvent ? buildFieldNotes(attempts, notes) : notes,
    }),
    onSuccess: () => {
      toast({ message: 'Result saved', severity: 'success' });
      qc.invalidateQueries({ queryKey: ['meets', meetId, 'meet-day-registrations'] });
      onClose();
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  return (
    <Dialog open={!!registration} onClose={onClose} fullWidth maxWidth={isFieldEvent ? 'md' : 'sm'}>
      <DialogTitle>
        {isFieldEvent ? 'Field event marks' : 'Record result'} · {registration?.student.full_name}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          {isFieldEvent && (
            <>
              <Grid item xs={12}>
                <Typography color="text.secondary">
                  Enter each attempt as it happens. Use Foul or Pass for messy paper marks, and Baton will use the best valid mark as the result.
                </Typography>
              </Grid>
              {attempts.map((attempt, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Stack spacing={1}>
                    <TextField
                      label={`Attempt ${idx + 1}`}
                      value={attempt}
                      onChange={(e) => updateAttempt(setAttempts, idx, e.target.value)}
                      placeholder="5.28m, 12-4, F, or P"
                      inputProps={{ 'aria-label': `Attempt ${idx + 1}` }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button fullWidth size="small" variant="outlined" color="error" onClick={() => updateAttempt(setAttempts, idx, 'F')} aria-label={`Mark attempt ${idx + 1} foul`}>
                        Foul
                      </Button>
                      <Button fullWidth size="small" variant="outlined" onClick={() => updateAttempt(setAttempts, idx, 'P')} aria-label={`Mark attempt ${idx + 1} pass`}>
                        Pass
                      </Button>
                      <Button fullWidth size="small" onClick={() => updateAttempt(setAttempts, idx, '')} aria-label={`Clear attempt ${idx + 1}`}>
                        Clear
                      </Button>
                    </Stack>
                  </Stack>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Best mark</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      {bestAttempt || 'No valid mark yet'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={4}>
            <TextField
              label={isFieldEvent ? 'Override best mark' : 'Mark / time'}
              value={mark}
              onChange={(e) => {
                setMark(e.target.value);
                setManualMarkEdited(true);
              }}
              placeholder={isFieldEvent ? bestAttempt || '5.28m' : '11.42 or 5.28m'}
              helperText={isFieldEvent && bestAttempt ? `Leaving this as-is saves ${bestAttempt}.` : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={4}><TextField label="Place" type="number" value={place} onChange={(e) => setPlace(e.target.value)} /></Grid>
          <Grid item xs={12} sm={4}><TextField label="Wind" type="number" value={wind} onChange={(e) => setWind(e.target.value)} /></Grid>
          <Grid item xs={12}>
            <TextField
              label={isFieldEvent ? 'Judge notes' : 'Notes'}
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Quick status"
              select
              defaultValue={registration?.status || 'registered'}
              onChange={(e) => registration && api.patch(`/registrations/${registration.id}`, { status: e.target.value }).then(() => qc.invalidateQueries({ queryKey: ['meets', meetId, 'meet-day-registrations'] }))}
            >
              <MenuItem value="registered">Registered</MenuItem>
              <MenuItem value="dns">DNS</MenuItem>
              <MenuItem value="dnf">DNF</MenuItem>
              <MenuItem value="dq">DQ</MenuItem>
              <MenuItem value="scratched">Scratched</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>Save result</Button>
      </DialogActions>
    </Dialog>
  );
}

function updateAttempt(setAttempts: React.Dispatch<React.SetStateAction<string[]>>, idx: number, value: string) {
  const normalized = ['f', 'p'].includes(value.trim().toLowerCase()) ? value.toUpperCase() : value;
  setAttempts((prev) => prev.map((attempt, i) => (i === idx ? normalized : attempt)));
}

function parseAttempts(notes?: string | null): string[] {
  const attempts = Array(ATTEMPT_COUNT).fill('');
  const line = (notes || '').split('\n').find((row) => row.startsWith('Attempts:'));
  if (!line) return attempts;
  line
    .replace('Attempts:', '')
    .split(',')
    .map((part) => part.trim())
    .forEach((part, idx) => {
      if (idx < ATTEMPT_COUNT) attempts[idx] = part.replace(/^A\d+:\s*/, '');
    });
  return attempts;
}

function buildFieldNotes(attempts: string[], notes: string): string {
  const attemptLine = `Attempts: ${attempts.map((attempt, idx) => `A${idx + 1}: ${attempt || '-'}`).join(', ')}`;
  const cleanedNotes = notes
    .split('\n')
    .filter((line) => !line.startsWith('Attempts:'))
    .join('\n')
    .trim();
  return cleanedNotes ? `${attemptLine}\n${cleanedNotes}` : attemptLine;
}

function bestFieldAttempt(attempts: string[]): string {
  const valid = attempts
    .map((attempt) => ({ attempt, value: fieldMarkValue(attempt) }))
    .filter((row): row is { attempt: string; value: number } => row.value !== null);
  valid.sort((a, b) => b.value - a.value);
  return valid[0]?.attempt ?? '';
}

function fieldMarkValue(mark: string): number | null {
  const trimmed = mark.trim().toUpperCase();
  if (!trimmed || ['F', 'FOUL', 'P', 'PASS', '-', 'DNS'].includes(trimmed)) return null;
  const feetInches = trimmed.match(/^(\d+)\s*[-']\s*(\d+(?:\.\d+)?)"?$/);
  if (feetInches) {
    return Number(feetInches[1]) * 12 + Number(feetInches[2]);
  }
  const numeric = trimmed.match(/\d+(?:\.\d+)?/);
  return numeric ? Number(numeric[0]) : null;
}
